import styled from 'styled-components';

export { SelectToggle } from '../../styles';

// Component Detail View Styles
export const Base = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow-y: auto;
`;

export const Header = styled.div`
	padding: 24px 20px;
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const HeaderContent = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 16px;
`;

export const BackButton = styled.button`
	display: flex;
	margin-top: 4px;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 6px;
	border: none;
	background: transparent;
	color: ${({ theme }) => theme.gray[700]};
	transition: background-color 0.15s ease, color 0.15s ease;
	outline: none;
	padding: 0;
	flex-shrink: 0;

	&:hover {
		background: ${({ theme }) => theme.gray[200]};
		color: ${({ theme }) => theme.gray[1000]};
	}
`;

export const Title = styled.h1`
	font-size: 32px;
	font-weight: 700;
	color: ${({ theme }) => theme.gray[1000]};
	margin: 0 0 12px 0;
`;

export const Meta = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 12px;
`;

export const Category = styled.span`
	font-size: 13px;
	color: ${({ theme }) => theme.gray[600]};
	background-color: ${({ theme }) => theme.gray[100]};
	padding: 4px 12px;
	border-radius: 12px;
	font-weight: 500;
	transition: all 0.15s ease;

	&:hover {
		background-color: ${({ theme }) => theme.gray[200]};
		color: ${({ theme }) => theme.gray[1000]};
	}
`;

export const Content = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 32px;
	padding: 24px 20px;
`;

export const PreviewSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
`;

// Props container with border
export const PropsContainer = styled.div`
	position: relative;
	z-index: 2;
	background-color: ${({ theme }) => theme.gray[0]};
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 8px;
	overflow: visible;
`;

// Props header with reset button on the right
export const PropsHeader = styled.div`
	display: flex;
	justify-content: flex-end;
	padding: 12px 16px;
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
	background-color: ${({ theme }) => theme.gray[100]};
`;

// Props controls grid
export const PropsControls = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 24px;
	padding: 20px;
`;

export const ControlGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

export const ControlLabel = styled.label`
	font-size: 12px;
	font-weight: 600;
	color: ${({ theme }) => theme.gray[800]};
	text-transform: uppercase;
	letter-spacing: 0.5px;
	display: flex;
	align-items: center;
	gap: 4px;
`;

export const RequiredMarker = styled.span`
	color: ${({ theme }) => theme.red[500]};
`;

export const ControlButtons = styled.div`
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
`;

export const ControlButton = styled.button<{ $active?: boolean }>`
	padding: 8px 16px;
	font-size: 13px;
	font-weight: 500;
	color: ${({ theme, $active }) => ($active ? theme.gray[0] : theme.gray[800])};
	background-color: ${({ theme, $active }) => ($active ? theme.blue[500] : theme.gray[0])};
	border: 1px solid ${({ theme, $active }) => ($active ? theme.blue[500] : theme.gray[200])};
	border-radius: 6px;
	transition: all 0.2s ease;

	&:hover {
		background-color: ${({ theme, $active }) => ($active ? theme.blue[600] : theme.gray[100])};
		border-color: ${({ theme, $active }) => ($active ? theme.blue[600] : theme.gray[400])};
	}
`;

export const PropTextInput = styled.input`
	padding: 8px 12px;
	font-size: 13px;
	color: ${({ theme }) => theme.gray[1000]};
	background-color: ${({ theme }) => theme.gray[0]};
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 6px;
	min-width: 150px;
	transition: all 0.2s ease;

	&:focus {
		outline: none;
		border-color: ${({ theme }) => theme.blue[500]};
	}

	&::placeholder {
		color: ${({ theme }) => theme.gray[400]};
	}
`;

export const PropSelect = styled.select`
	padding: 8px 12px;
	font-size: 13px;
	color: ${({ theme }) => theme.gray[1000]};
	background-color: ${({ theme }) => theme.gray[0]};
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 6px;
	min-width: 150px;
	transition: all 0.2s ease;

	&:focus {
		outline: none;
		border-color: ${({ theme }) => theme.blue[500]};
	}
`;

export const JsonInputWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

export const JsonTextarea = styled.textarea<{ $hasError?: boolean }>`
	padding: 8px 12px;
	font-size: 12px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	color: ${({ theme }) => theme.gray[1000]};
	background-color: ${({ theme }) => theme.gray[0]};
	border: 1px solid ${({ theme, $hasError }) => ($hasError ? theme.red[500] : theme.gray[200])};
	border-radius: 6px;
	min-width: 200px;
	min-height: 60px;
	resize: vertical;
	transition: all 0.2s ease;

	&:focus {
		outline: none;
		border-color: ${({ theme, $hasError }) => ($hasError ? theme.red[500] : theme.blue[500])};
	}
`;

export const JsonError = styled.div`
	font-size: 11px;
	color: ${({ theme }) => theme.red[500]};
`;

export const ResetButton = styled.button`
	padding: 6px 12px;
	font-size: 12px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[700]};
	background: transparent;
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 6px;
	transition: all 0.2s ease;

	&:hover {
		background-color: ${({ theme }) => theme.gray[100]};
		color: ${({ theme }) => theme.gray[1000]};
		border-color: ${({ theme }) => theme.gray[400]};
	}
`;

export const PreviewWrapper = styled.div`
	position: relative;
	width: 100%;
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 8px;
	overflow: hidden;

	iframe {
		width: 100%;
		border: none;
		display: block;
	}
`;

export const DocumentationSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 32px;
	margin-top: 8px;
`;

export const CodeBlock = styled.div`
	display: flex;
	flex-direction: column;
`;

export const CodeHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: 2px solid ${({ theme }) => theme.gray[200]};
	padding: 0 4px;
`;

export const CodeTabs = styled.div`
	display: flex;
	gap: 0;
`;

export const CodeTab = styled.button<{ $active?: boolean }>`
	padding: 10px 16px;
	font-size: 14px;
	font-weight: ${({ $active }) => ($active ? 600 : 500)};
	color: ${({ theme, $active }) => ($active ? theme.gray[1000] : theme.gray[600])};
	background: transparent;
	border: none;
	border-bottom: 2px solid ${({ theme, $active }) => ($active ? theme.gray[1000] : 'transparent')};
	margin-bottom: -2px;
	transition: color 0.15s ease, border-color 0.15s ease;

	&:hover {
		color: ${({ theme }) => theme.gray[1000]};
	}
`;

export const CodeCopyButton = styled.button`
	font-size: 12px;
	color: ${({ theme }) => theme.blue[500]};
	background: none;
	border: 1px solid ${({ theme }) => theme.gray[200]};
	padding: 4px 12px;
	border-radius: 4px;
	transition: all 0.2s ease;

	&:hover {
		background-color: ${({ theme }) => theme.gray[100]};
		border-color: ${({ theme }) => theme.blue[500]};
	}
`;

export const CodeContent = styled.div`
	padding: 20px 16px;
	min-height: 400px;
	-webkit-user-select: text;
	-moz-user-select: text;
	-ms-user-select: text;
	user-select: text;

	pre {
		margin: 0;
		font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
			'Courier New', monospace;
		font-size: 13px;
		line-height: 1.6;
		color: ${({ theme }) => theme.gray[1000]};
		white-space: pre-wrap;
		word-wrap: break-word;
		-webkit-user-select: text;
		-moz-user-select: text;
		-ms-user-select: text;
		user-select: text;
	}
`;

export const DepsContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
`;

export const DepsGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

export const DepsGroupLabel = styled.div`
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: ${({ theme }) => theme.gray[600]};
`;

export const DepsPills = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
`;

export const DepsEmpty = styled.div`
	font-size: 13px;
	color: ${({ theme }) => theme.gray[400]};
	padding: 20px 0;
`;

export const BrokenBadge = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 10px;
	height: 16px;
	padding: 0 4px;
	margin-left: 6px;
	border-radius: 8px;
	font-size: 10px;
	font-weight: 600;
	background-color: ${({ theme }) => theme.red[500]};
	color: ${({ theme }) => theme.gray[0]};
`;

export const DepsAutofix = styled.div`
	display: flex;
	justify-content: flex-start;
	padding-top: 8px;
	border-top: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const AutofixButton = styled.button`
	display: flex;
	align-items: center;
	padding: 4px 10px;
	font-size: 13px;
	font-weight: 500;
	border-radius: 6px;
	transition: all 0.15s ease;
	white-space: nowrap;
	border: 1px solid ${({ theme }) => theme.blue[500]};
	color: ${({ theme }) => theme.gray[1000]};
	background-color: ${({ theme }) => theme.blue[500]};

	&:hover {
		background-color: ${({ theme }) => theme.blue[400]};
		border-color: ${({ theme }) => theme.blue[400]};
	}

	&:disabled {
		opacity: 0.5;
	}
`;
